"use client";
import { LogoClucth } from "@/lib/images";
import { motion } from 'framer-motion';
import Image from "next/image";
import { ReactNode } from 'react';
import Marquee from "react-fast-marquee";

// Import SVG logos directly in the component
const GoogleLogo = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
);

// Clutch Logo now uses the imported image
const ClutchLogo = () => (
    <div className="relative w-full h-full flex items-center justify-center">
        <Image
            src={LogoClucth}
            alt="Clutch Logo"
            className="w-6 h-6 md:w-7 md:h-7 object-contain"
        />
    </div>
);

const UpworkLogo = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7">
        <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3-2.439-5.439-5.439-5.439z" fill="#14a800" />
    </svg>
);

// Define interface for ReviewCard props
interface ReviewCardProps {
    logo: ReactNode;
    stars: number;
    rating: string;
    reviewCount: string;
    reviewText: string;
    delay?: number;
    bgColor: string;
    starColor: string;
    href?: string; // Add optional href prop
}

// Review card component to reuse
const ReviewCard = ({ logo, stars, rating, reviewCount, reviewText, delay = 0, bgColor, starColor, href }: ReviewCardProps) => {
    const cardContent = (
        <div className="flex items-center p-3 md:p-4 rounded-xl bg-white transition-all duration-300 border border-gray-200 hover:shadow-lg">
            {/* Logo */}
            <div className="flex-shrink-0 mr-3 md:mr-4">
                <div className="relative">
                    <motion.div
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white flex items-center justify-center overflow-hidden border border-gray-100"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                    >
                        {logo}
                    </motion.div>
                </div>
            </div>

            {/* Rating content */}
            <div className="flex flex-col">
                <div className="flex items-center mb-1">
                    {/* Stars */}
                    <div className="flex">
                        {[...Array(5)].map((_, i) => (
                            <svg key={i} className={`w-4 h-4 text-${starColor} fill-${starColor}`} viewBox="0 0 24 24">
                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                            </svg>
                        ))}
                    </div>

                    {/* Rating number */}
                    <div className="ml-2 font-semibold text-gray-800">{rating} </div>
                </div>

                {/* Review count */}
                <div className="text-sm text-gray-600">
                    <span className="font-medium">{reviewCount}</span> <span>{reviewText}</span>
                </div>
            </div>
        </div>
    );

    return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="block">
            {cardContent}
        </a>
    ) : (
        cardContent
    );
};

// Define interface for review card data
interface ReviewCardData {
    logo: ReactNode;
    stars: number;
    rating: string;
    reviewCount: string;
    reviewText: string;
    delay: number;
    bgColor: string;
    starColor: string;
    href?: string; // Add optional href prop
}

const ReviewWidgetDemo = () => {
    const reviewCards: ReviewCardData[] = [
        {
            logo: <GoogleLogo />,
            stars: 5,
            rating: "5.0",
            reviewCount: "5",
            reviewText: "Google Reviews",
            delay: 0,
            bgColor: "blue-500",
            starColor: "yellow-400",
            href: "https://share.google/k1W3pfFGyZBDsYVo3"
        },
        {
            logo: <ClutchLogo />,
            stars: 5,
            rating: "5.0",
            reviewCount: "3",
            reviewText: "Clutch Reviews",
            delay: 0.2,
            bgColor: "purple-500",
            starColor: "red-500",
            href: "https://clutch.co/profile/devkrest-technologies"
        },
        {
            logo: <UpworkLogo />,
            stars: 5,
            rating: "5.0",
            reviewCount: "20",
            reviewText: "Upwork Reviews",
            delay: 0.4,
            bgColor: "green-500",
            starColor: "green-500",
            href: "https://www.upwork.com/freelancers/~01a4c62ff3884cd1f1?mp_source=devkrest"
        }
    ];

    return (
        <div className="w-full p-4 md:p-6 flex flex-col items-center justify-center">
            <div className="hidden md:flex w-full flex-row items-stretch justify-center gap-6">
                {reviewCards.map((card, index) => (
                    <ReviewCard key={index} {...card} />
                ))}
            </div>
            <div className="md:hidden w-full overflow-hidden">
                <Marquee
                    speed={30}
                    pauseOnHover={true}
                    gradient={true}
                    gradientWidth={20}
                >
                    <div className="flex gap-4 px-2">
                        {reviewCards.map((card, index) => (
                            <div key={index} className="mx-2" style={{ minWidth: '200px' }}>
                                <ReviewCard {...card} />
                            </div>
                        ))}
                    </div>
                </Marquee>
            </div>
        </div>
    );
};

export default ReviewWidgetDemo; 